"use client";

import type { FormEvent } from "react";
import { Search } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Chip } from "@/components/ui/chip";
import { lazyComponent } from "@/components/ui/lazy-component";
import { LoadFailureNotice } from "@/components/ui/load-failure-notice";
import type { SupportSearchEntry } from "@/lib/support/support-search";
import { SupportAnswerPending } from "./support-answer-pending";
import { SupportSearchResults } from "./support-search-results";
import { useSupportAsk } from "./use-support-ask";
import { useSupportSearch, type SupportFaq } from "./use-support-search";

const SupportConversation = lazyComponent(
  () => import("./support-conversation").then((mod) => mod.SupportConversation),
  {
    ssr: false,
    loading: (
      <div aria-live="polite" className="px-5 py-5">
        <SupportAnswerPending />
      </div>
    ),
    failure: {
      label: "support-conversation",
      fallback: <LoadFailureNotice title="The answer could not load." />,
    },
  },
);

const SUGGESTED_QUESTIONS = [
  "How do I read a Bill of Lading?",
  "Grade A vs grade B gaylords",
  "What fits on a 53 ft trailer?",
  "How do recycling pickups work?",
  "When do sellers get paid?",
] as const;

function AskInput({
  value,
  onChange,
  onSubmit,
  isFollowUp,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isFollowUp: boolean;
}) {
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit();
  }
  return (
    <form role="search" onSubmit={handleSubmit} className="flex items-center gap-3 py-2.5 pl-5 pr-2.5 focus-within:ring-2 focus-within:ring-inset focus-within:ring-ring/40">
      <Search className="size-5 shrink-0 text-muted-foreground" aria-hidden />
      <label htmlFor="support-ask" className="sr-only">
        Ask a question or search the site
      </label>
      <input
        id="support-ask"
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete="off"
        enterKeyHint="search"
        placeholder={isFollowUp ? "Ask a follow-up" : "Ask a question or search"}
        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base text-foreground placeholder:text-fg-subtle focus:outline-none focus:ring-0 md:text-lg"
      />
      <Button type="submit" variant="primary">
        Ask
      </Button>
    </form>
  );
}

function SuggestedQuestions({ onAsk }: { onAsk: (question: string) => void }) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <span className="shrink-0 text-sm font-medium text-muted-foreground">People ask</span>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
        {SUGGESTED_QUESTIONS.map((question) => (
          <Chip key={question} asChild size="lg">
            <button type="button" onClick={() => onAsk(question)}>
              {question}
            </button>
          </Chip>
        ))}
      </div>
    </div>
  );
}

export function SupportAskBox({
  entries,
  faqs,
}: {
  entries: ReadonlyArray<SupportSearchEntry>;
  faqs: ReadonlyArray<SupportFaq>;
}) {
  const support = useSupportAsk();
  const { askedQuestion } = support;
  const hasAsked = askedQuestion !== null;
  const search = useSupportSearch(hasAsked ? "" : support.query, entries, faqs);
  return (
    <div className="space-y-4">
      <Card padding="none" className="overflow-hidden">
        <AskInput
          value={support.query}
          onChange={support.setQuery}
          onSubmit={() => support.ask(support.query)}
          isFollowUp={hasAsked}
        />
        {askedQuestion !== null && (
          <div className="border-t border-border">
            <SupportConversation
              question={askedQuestion}
              sendFollowUpRef={support.sendFollowUpRef}
              onStartOver={support.startOver}
            />
          </div>
        )}
        {!hasAsked && search.isActive && (
          <div className="border-t border-border">
            <SupportSearchResults groups={search.groups} isSearching={search.isSearching} />
          </div>
        )}
      </Card>
      {!hasAsked && <SuggestedQuestions onAsk={support.ask} />}
    </div>
  );
}
